import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteNav from '../components/SiteNav';
import PageMeta from '../components/PageMeta';
import { ArrowRight, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HEADSHOT_URL = `${API_URL}/api/files/email-assets/me_headshot.png`;

const About = () => {
  const navigate = useNavigate();
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyNav(window.scrollY > 200);
      setShowScrollTop(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">

      {/* ── Navigation Bar — canonical shared HiBFF menu ── */}
      <PageMeta
        title="About HiBFF — Why We Built a Social-Confidence Platform for Teens"
        description="HiBFF helps teens aged 13-19 build real-world social confidence by practising conversations with safety-checked AI tutors — with parents approving every account."
        path="/about"
        slug="about"
      />
      <SiteNav current="about" mode="fixed" />

      {/* ── Sticky compact nav removed — the primary nav above is already
            fixed and visible on scroll, so a second compact bar would
            stack on top and create a double-header banner. ── */}

      {/* ═══════════════════════════════════════════════════════════════
          Section 1 — The Founder Story
      ═══════════════════════════════════════════════════════════════ */}
      <section className="pt-44 pb-20 px-4" data-testid="section-founder-story">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#F5831F' }}>The Founder Story</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight" style={{ fontFamily: 'Georgia, serif', color: '#1A1209' }}>
            I built this because I deleted everything.
          </h1>
          <p className="text-base font-semibold mb-1" style={{ fontFamily: 'Georgia, serif', color: '#1A1209' }} data-testid="about-brand-line">
            HiBFF — Hi Brave Friends Forever
          </p>
          <p className="text-sm italic mb-8 max-w-2xl" style={{ fontFamily: 'Georgia, serif', color: '#7B4FD8' }} data-testid="about-brand-tagline">
            Brave takes courage. Courage builds confidence. Confidence builds character. And character is the foundation of friends forever.
          </p>
          <div className="text-base leading-relaxed space-y-5" style={{ fontFamily: 'Georgia, serif', color: '#444' }}>
            <p>I launched HiBFF twice before this version. Each time, I watched it drift toward something I wasn&apos;t proud of &mdash; AI companions, digital relationships, more reasons to stay on a screen. So I deleted it. The website. The AWS stack. The press campaign. Everything.</p>
            <p>Then I spent over 100 hours a week building something I could actually stand behind.</p>
            <p>HiBFF is built on a simple belief: the world changed after Covid, and an entire generation of teenagers lost the years when social confidence is supposed to form naturally. I want to give that back to them &mdash; not through more screen time, but through practice. Real challenges. Real growth. And ultimately, real human connection.</p>
            <p>I know it works because I did it myself. Every single day, walking through London, I would stop a stranger and say something genuinely kind &mdash; then walk away without waiting for anything in return. It changed how I felt about myself. It changed how they started their day. That&apos;s what HiBFF is.</p>
          </div>
          <div className="flex items-center gap-4 mt-10" data-testid="founder-attribution">
            <img
              src={HEADSHOT_URL}
              alt="Robert Parson"
              className="rounded-full object-cover"
              style={{ width: '64px', height: '64px' }}
              data-testid="founder-headshot-s1"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <p className="text-base font-bold" style={{ color: '#1A1209' }}>Robert Parson</p>
              <p className="text-sm" style={{ color: '#999' }}>Founder & CEO, HiBFF</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          Section 2 — The Mission
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4" style={{ backgroundColor: '#FFF5EA' }} data-testid="section-mission">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#F5831F' }}>The Mission</p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Georgia, serif', color: '#1A1209' }}>
            Hi, Brave Friends Forever.
          </h2>
          <p className="text-base leading-relaxed" style={{ fontFamily: 'Georgia, serif', color: '#444' }}>
            HiBFF exists to help you need it less. It is a practice tool, not a replacement for real human connection. Every feature, every AvA, every challenge, every badge &mdash; all of it is designed to help teenagers feel more confident out there, with real people, doing real things.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          Section 3 — How It Works  (REMOVED per AboutPage Dev Brief, May 2026
          — already lives on the homepage; kept only there to avoid duplication.)

          Section 4 — Built Differently  (REMOVED per same brief — kept on
          homepage and Safety page.)
      ═══════════════════════════════════════════════════════════════ */}

      {/* ═══════════════════════════════════════════════════════════════
          Section 3 — How It Works  (REMOVED per AboutPage Dev Brief, May 2026
          — already lives on the homepage; kept only there to avoid duplication.)

          Section 4 — Built Differently  (REMOVED per same brief — kept on
          homepage and Safety page.)
      ═══════════════════════════════════════════════════════════════ */}

      {/* ═══════════════════════════════════════════════════════════════
          Section 5 — About Robert
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4" data-testid="section-about-robert">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#F5831F' }}>The Person Behind It</p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-10" style={{ fontFamily: 'Georgia, serif', color: '#1A1209' }}>
            About Robert
          </h2>
          <div className="flex flex-col sm:flex-row gap-8 items-center sm:items-start">
            <img
              src={HEADSHOT_URL}
              alt="Robert Parson"
              className="rounded-full object-cover shrink-0"
              style={{ width: '100px', height: '100px' }}
              data-testid="robert-headshot"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="text-base leading-relaxed space-y-4" style={{ fontFamily: 'Georgia, serif', color: '#444' }}>
              <p>Robert Parson is the Founder and CEO of HiBFF. He has spent forty years building businesses across multiple countries &mdash; from the UK to continental Europe and beyond. HiBFF is his most personal build. Not because it is his most technically complex &mdash; though at 285,000+ lines of code and 1,200+ endpoints, it is &mdash; but because it is the first time he has built something he would have wanted to exist when he was young.</p>
              <p>He launched HiBFF twice before this version. Each time he watched it drift toward something he was not proud of. The second time he deleted everything &mdash; the codebase, the AWS stack, the press campaign &mdash; and started again. Not because the technology failed. Because he looked at where the market was heading and realised he was building something that hurt teenagers rather than helped them.</p>
              <p>The version you are looking at was built in 2,265 hours of focused work. It is self-funded and independent. No venture capital. No investor agenda. No pressure to maximise engagement at the expense of the teenagers using it.</p>
              <p>Robert has filed three UK patent applications covering the core HiBFF methodology &mdash; GB2617027.4 (Safety-Gated AI Social Skills Practice), GB2622892.4 (Fail-Safe Escalation &amp; Warm Handover) and GB2622997.1 (Guardian-Gated Custody of Minors&rsquo; Digital Assets), all live at the UK IPO &mdash; with a further filing programme planned once investment is in place. He is relocating HiBFF&rsquo;s operations permanently to Abu Dhabi, where HiBFF Ltd will be established in the Abu Dhabi Global Market (ADGM) &mdash; structured for the long term and positioned to serve families and schools worldwide.</p>
              <p>He reads every email sent to HiBFF personally. If you have a question, reply to any HiBFF email. It goes to him.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          Section 6 — Contact
      ═══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4" style={{ backgroundColor: '#FFF5EA' }} data-testid="section-contact">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#F5831F' }}>Get In Touch</p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-12" style={{ fontFamily: 'Georgia, serif', color: '#1A1209' }}>
            Contact
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { label: 'For parents', email: 'parents@hibff.com' },
              { label: 'For schools', email: 'schools@hibff.com' },
              { label: 'For organisations', email: 'business@hibff.com' },
            ].map(({ label, email }, i) => (
              <a
                key={i}
                href={`mailto:${email}`}
                className="bg-white rounded-xl p-6 transition-all duration-200 hover:shadow-md"
                style={{ border: '1px solid #E5E7EB' }}
                data-testid={`contact-${email.split('@')[0]}`}
              >
                <Mail size={22} style={{ color: '#7B4FD8' }} className="mx-auto mb-3" />
                <p className="text-sm font-semibold mb-1" style={{ color: '#1A1209' }}>{label}</p>
                <p className="text-sm" style={{ color: '#7B4FD8' }}>{email}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 px-4" style={{ backgroundColor: '#1A1209' }} data-testid="section-cta">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Ready to start?
          </h2>
          <p className="text-gray-400 mb-8">Your teenager&apos;s journey to real-world confidence begins with a single step.</p>
          <button
            onClick={() => navigate('/register')}
            className="inline-flex items-center gap-2 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-200 hover:opacity-90"
            style={{ backgroundColor: '#F5831F' }}
            data-testid="about-cta-btn"
          >
            Get Started <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-8 px-4 bg-gray-900 text-white" data-testid="about-footer">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-6">
            <div>
              <h4 className="font-bold mb-3">Product</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/register?persona=teen" className="hover:text-white" data-testid="footer-product-teens">Teens</a></li>
                <li><a href="/parent-first" className="hover:text-white" data-testid="footer-product-parents">Parents</a></li>
                <li><a href="/register?persona=adult" className="hover:text-white" data-testid="footer-product-adults">Adults</a></li>
                <li><a href="/education" className="hover:text-white" data-testid="footer-product-schools">Education</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/about" className="hover:text-white">About</a></li>
                <li><a href="/investor-portal" className="hover:text-white" data-testid="footer-investors-link">Investor Portal</a></li>
                <li><a href="https://shop.hibff.com" target="_blank" rel="noopener noreferrer" className="hover:text-white">Shop</a></li>
                <li><a href="/partners" className="hover:text-white">Partners</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">For Parents</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/privacy" className="hover:text-white">Teen Safety &amp; Privacy</a></li>
                <li><a href="/child-safety" className="hover:text-white">Child Safety Standards</a></li>
                <li><a href="/delete-account" className="hover:text-white">Delete Your Account</a></li>
                <li><a href="/docs" className="hover:text-white">Parent Dashboard Guide</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/terms" className="hover:text-white">Terms &amp; Conditions</a></li>
                <li><a href="/privacy" className="hover:text-white">Privacy &amp; Data Safety Policy</a></li>
                <li><a href="/refund-policy" className="hover:text-white">Refund Policy</a></li></ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="mailto:parents@hibff.com" className="hover:text-white">parents@hibff.com</a></li>
                <li><a href="mailto:schools@hibff.com" className="hover:text-white">schools@hibff.com</a></li>
                <li><a href="mailto:business@hibff.com" className="hover:text-white">business@hibff.com</a></li></ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-4 text-center">
            <p className="text-xs text-gray-500">HiBFF is protected by 3 live UK patent applications (GB2617027.4 &middot; GB2622892.4 &middot; GB2622997.1), all filed at the UK IPO. &copy; HiBFF Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* ── Scroll to top ── */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-6 sm:bottom-8 right-6 sm:right-8 z-[60] w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg ${showScrollTop ? 'opacity-100 scale-100' : 'opacity-0 scale-0 pointer-events-none'}`}
        style={{ backgroundColor: '#7B4FD8' }}
        data-testid="scroll-to-top"
        aria-label="Scroll to top"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg>
      </button>
    </div>
  );
};

export default About;
